import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import "./customerProfile.css";
import { useCustomerContext } from "../../../context/CustomerContext";
import {
  FaUser,
  FaPhone,
  FaMoneyBillWave,
  FaCalendar,
  FaFileInvoice,
} from "react-icons/fa";

const CustomerProfile = () => {
  const { id } = useParams();
  const { getCustomerProfile } = useCustomerContext();

  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCustomerData();
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const data = await getCustomerProfile(id);
      setCustomer(data.customer);
      setInvoices(data.invoices || []);
      setStatistics(data.statistics);
      setError(null);
    } catch (err) {
      setError("فشل في جلب بيانات الزبون");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("ar-EG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="customer-profile-container">
        <div className="loading">جاري التحميل...</div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="customer-profile-container">
        <div className="error-box">{error || "الزبون غير موجود"}</div>
      </div>
    );
  }

  return (
    <div className="customer-profile-container">
      <div className="wrapper">
        {/* Header */}
        <div className="profile-header">
          <div className="header-content">
            <div className="customer-avatar">
              <FaUser />
            </div>
            <div className="header-info">
              <h1>{customer.full_name}</h1>
              <p className="join-date">
                <FaCalendar /> انضم في {formatDate(customer.created_at)}
              </p>
            </div>
          </div>
          <Link to={`/update-customer/${id}`} className="edit-btn">
            تعديل البيانات
          </Link>
        </div>

        {/* Statistics Cards */}
        <div className="statistics-grid">
          <div className="stat-card balance-card">
            <div className="stat-icon">
              <FaMoneyBillWave />
            </div>
            <div className="stat-info">
              <p className="stat-label">الرصيد الحالي</p>
              <h3
                className="stat-value"
                style={{
                  color:
                    customer.balance < 0
                      ? "#ef4444"
                      : customer.balance > 0
                      ? "#10b981"
                      : "#1f2937",
                }}
              >
                {parseFloat(customer.balance).toFixed(2)} ₪
              </h3>
            </div>
          </div>

          <div className="stat-card invoices-card">
            <div className="stat-icon">
              <FaFileInvoice />
            </div>
            <div className="stat-info">
              <p className="stat-label">عدد الفواتير</p>
              <h3 className="stat-value">{statistics?.total_invoices || 0}</h3>
            </div>
          </div>

          <div className="stat-card total-card">
            <div className="stat-icon">
              <FaMoneyBillWave />
            </div>
            <div className="stat-info">
              <p className="stat-label">إجمالي المبيعات</p>
              <h3 className="stat-value">
                {parseFloat(statistics?.total_amount || 0).toFixed(2)} ₪
              </h3>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="info-section">
          <h2>معلومات الاتصال</h2>
          <div className="info-grid">
            <div className="info-item">
              <FaPhone className="info-icon" />
              <div>
                <p className="info-label">رقم الهاتف</p>
                <p className="info-value">{customer.phone || "غير متوفر"}</p>
              </div>
            </div>

            <div className="info-item">
              <FaUser className="info-icon" />
              <div>
                <p className="info-label">الوصف</p>
                <p className="info-value">
                  {customer.description || "غير متوفر"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sales Invoices Table */}
        <div className="invoices-section">
          <h2>فواتير البيع</h2>
          {invoices.length === 0 ? (
            <div className="no-data">لا توجد فواتير بيع لهذا الزبون</div>
          ) : (
            <div className="table-container">
              <table className="invoices-table">
                <thead>
                  <tr>
                    <th>رقم الفاتورة</th>
                    <th>التاريخ</th>
                    <th>عدد الأصناف</th>
                    <th>المبلغ الإجمالي</th>
                    <th>الحالة</th>
                    <th>ملاحظات</th>
                    <th>إجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>#{invoice.id}</td>
                      <td>{formatDate(invoice.created_at)}</td>
                      <td>{invoice.items_count}</td>
                      <td className="amount">
                        {parseFloat(invoice.total_amount).toFixed(2)} ₪
                      </td>
                      <td>
                        {invoice.is_canceled == 1 ? (
                          <span
                            style={{
                              padding: "4px 12px",
                              backgroundColor: "#fee2e2",
                              color: "#dc2626",
                              borderRadius: "4px",
                              fontWeight: "600",
                              fontSize: "13px",
                            }}
                          >
                            ملغية
                          </span>
                        ) : (
                          <span
                            style={{
                              padding: "4px 12px",
                              backgroundColor: "#d1fae5",
                              color: "#059669",
                              borderRadius: "4px",
                              fontWeight: "600",
                              fontSize: "13px",
                            }}
                          >
                            نشطة
                          </span>
                        )}
                      </td>
                      <td className="notes">{invoice.notes || "-"}</td>
                      <td>
                        <Link
                          to={`/sales-invoice/${invoice.id}`}
                          className="view-btn"
                        >
                          عرض
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;
